import type { BusinessDoc } from '../api/converters.js';

/**
 * Is this business paid up? (doc 23)
 *
 * ONE statement of the rule, imported by the subscribe screen, the settings
 * card, the setup checklist and the wizard.
 *
 * It lives in `shared/lib` rather than in the billing feature for the same
 * reason `isReachable` does: onboarding has to ask "is this business paid up?"
 * and billing has to ask "is this owner still in setup?", and a predicate owned
 * by either feature makes those two barrels import each other. A pure question
 * about a business document belongs to neither. The same mistake the setup checklist made before
 * `isReachable` existed: three surfaces each deciding "is it subscribed" for
 * themselves is three chances to tell an owner different things about whether
 * they have paid.
 *
 * `past_due` counts as subscribed, deliberately. Stripe retries a failed card
 * over several days before giving up, and during that window the owner has a
 * working product and a card that needs attention — not a cancelled account.
 * Cutting service on the first failed charge would take a salon's phone down
 * over an expired card. What `past_due` earns is a loud banner, not a lockout;
 * `canceled` is what Stripe reports once the retries are exhausted.
 */
export function isSubscribed(business: BusinessDoc): boolean {
  const { status } = business.subscription;
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

/** Paid AND healthy — nothing for the owner to fix. */
export function isBillingHealthy(business: BusinessDoc): boolean {
  const { status } = business.subscription;
  return status === 'active' || status === 'trialing';
}

/** Never subscribed, or the subscription has lapsed for good. The only state
 *  that should send an owner back to the plan picker. */
export function needsSubscription(business: BusinessDoc): boolean {
  return !isSubscribed(business);
}
